// Creative products and Creative Version lifecycle.

/** Commercial products V1 (PRD-V1.md §1, ARCHITECTURE-V1.md §4.1). */
export const CREATIVE_PRODUCTS = [
  "RICH_MEDIA_STANDARD",
  "VIDEO_RICH_MEDIA",
  "ADVERGAMING",
  "SURVEY_RICH_MEDIA",
] as const;

export type CreativeProduct = (typeof CREATIVE_PRODUCTS)[number];

/**
 * Creative Version lifecycle states, in the order of PRD-V1.md §11:
 * DRAFT → PREVIEW → CLIENT_REVIEW → APPROVED → PUBLISHED → ARCHIVED.
 */
export const CREATIVE_VERSION_STATES = [
  "DRAFT",
  "PREVIEW",
  "CLIENT_REVIEW",
  "APPROVED",
  "PUBLISHED",
  "ARCHIVED",
] as const;

export type CreativeVersionState = (typeof CREATIVE_VERSION_STATES)[number];

/**
 * Minimal allowed transitions (Checkpoint 7.2, ADR-0009), enforced by
 * PostgreSQL triggers and mirrored here. A version never returns to DRAFT:
 * "changes requested" archives the reviewed version and the change is made
 * in a NEW draft (ADR-0005). PREVIEW → APPROVED covers internal approval
 * without a client-review step (PRD-V1.md §4.1 "Aprobación interna").
 * A PUBLISHED version can be archived only while no Distribution has it as
 * active version (checked in the database). Whether a dedicated
 * CHANGES_REQUESTED state is needed stays OPEN for 7.4.
 */
export const CREATIVE_VERSION_TRANSITIONS: Readonly<Record<CreativeVersionState, readonly CreativeVersionState[]>> = {
  DRAFT: ["PREVIEW", "ARCHIVED"],
  PREVIEW: ["CLIENT_REVIEW", "APPROVED", "ARCHIVED"],
  CLIENT_REVIEW: ["APPROVED", "ARCHIVED"],
  APPROVED: ["PUBLISHED", "ARCHIVED"],
  PUBLISHED: ["ARCHIVED"],
  ARCHIVED: [],
};

export function isCreativeVersionTransitionAllowed(from: CreativeVersionState, to: CreativeVersionState): boolean {
  return CREATIVE_VERSION_TRANSITIONS[from].includes(to);
}

/**
 * ADR-0005 / D10: a Creative Version is editable only in DRAFT. Once it
 * leaves DRAFT it is an immutable snapshot; any change creates a new DRAFT
 * version. Previously reviewed/approved/published content is never mutated.
 */
export function isCreativeVersionEditable(state: CreativeVersionState): boolean {
  return state === "DRAFT";
}

/**
 * Only a PUBLISHED version can be a Distribution's active version
 * (ADR-0009). Publishing = APPROVED → PUBLISHED plus pointing the
 * Distribution to it, in one transaction. A rollback points back to an
 * earlier version that is still PUBLISHED.
 */
export function isCreativeVersionActivatable(state: CreativeVersionState): boolean {
  return state === "PUBLISHED";
}

/**
 * Asset kinds of the Creative Document v2 (CREATIVE-MODEL-V1.md §9). Shared by
 * the control plane (asset tables) and the Creative Engine (pinned references).
 */
export const ASSET_KINDS = ["image", "video", "audio", "font", "captions"] as const;

export type AssetKind = (typeof ASSET_KINDS)[number];

/**
 * Upload ceilings per asset kind, in bytes (7.6G.2: ONE table read by the API, which enforces
 * them on the bytes it receives, and by the web, which states them before a person uploads).
 * PROVISIONAL engineering limits (the whole upload is buffered in memory), not platform limits.
 */
export const ASSET_UPLOAD_LIMITS = {
  image: 10 * 1024 * 1024,
  font: 2 * 1024 * 1024,
  video: 25 * 1024 * 1024,
  audio: 10 * 1024 * 1024,
} as const satisfies Partial<Record<AssetKind, number>>;

/** Origin of an immutable asset revision (CREATIVE-MODEL-V1.md §9.1). 7.6C creates `upload` only. */
export const ASSET_SOURCES = ["upload", "ai_generated", "derived_render", "derived_transform"] as const;

export type AssetSource = (typeof ASSET_SOURCES)[number];

/**
 * What InteractAds AI may do with a brand asset (7.6F.1, ADR-0023 §3; CREATIVE-MODEL-V1.md §9.3).
 *
 *   protected  the exact uploaded revision is authoritative: AI may place it, resize it
 *              proportionally, move it, show/hide it, fade it and animate the layer —
 *              never redraw, approximate, regenerate, recolor, crop or replace it.
 *   reference  as protected, and AI may ALSO use it as a visual reference to generate NEW
 *              related assets (their own asset, revision, hash and provenance).
 *   editable   as reference, and AI may ALSO produce transformed versions (crop, background
 *              removal, recolor…) — always as NEW assets, never by mutating this one.
 *
 * An uploaded asset starts `editable` (owner decision 2026-11-04, ADR-0052, migration 0038): the person's own
 * materials can be transformed into NEW assets when they ask; the original never changes. `protected` is an
 * explicit choice of whoever manages the brand (used exactly as uploaded). Assets uploaded before 0038 keep the
 * value they had. Publication copies (`derived_render`) are still created `protected`.
 */
export const ASSET_AI_USAGES = ["protected", "reference", "editable"] as const;

export type AssetAiUsage = (typeof ASSET_AI_USAGES)[number];

export const DEFAULT_ASSET_AI_USAGE: AssetAiUsage = "editable";

/** Kinds of AI use of an asset (7.6G will request them; the control plane enforces them). */
export type AssetAiUse = "reuse_exact" | "generate_from_reference" | "transform";

/** Deterministic permission check (never delegated to a model). */
export function assetAiAllows(usage: AssetAiUsage, use: AssetAiUse): boolean {
  if (use === "reuse_exact") return true;
  if (use === "generate_from_reference") return usage === "reference" || usage === "editable";
  return usage === "editable";
}
